import React, {useEffect, useRef} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import {browserHistory, WithRouterProps} from 'react-router';

import {
  useAnswerQuestion,
  useAttempt,
  useCompleteAttempt,
  useQuiz,
} from 'app/quizzes/api';
import {AttemptQuizContent} from 'app/quizzes/components/AttemptQuizPage/AttemptQuizContent';
import {TakeQuizPage} from 'app/quizzes/components/TakeQuizPage/TakeQuizPage';
import {sweetAPI} from 'app/store/sweet-api';
import {useModelChangeSubscription} from 'app/subscriptions';
import {useAsyncActions} from 'app/utils/redux';
import {QUIZ_TAGS} from 'app/utils/rtk-tags';

import type {AttemptAnswerType, QuestionType} from 'sweet-types';

type Props = WithRouterProps<{
  hashid: string;
  versionid: null | string;
  attemptid?: string;
}>;

export function AttemptQuizPage({params}: Props) {
  const {attemptid: attemptId, versionid: versionId, hashid} = params;
  const {data: attempt, refetch: refetchAttempt} = useAttempt(
    attemptId != null ? attemptId : skipToken
  );
  const {data: quiz} = useQuiz(
    attempt?.quiz != null ? (attempt.quiz.id as unknown as string) : skipToken
  );
  const [completeAttempt] = useCompleteAttempt();
  const [answerQuestion, {isLoading: isAnsweringQuestion}] =
    useAnswerQuestion();
  const dateCreateAt = useRef<string>('');

  const actions = useAsyncActions({
    invalidateTags: sweetAPI.util.invalidateTags,
  });

  useModelChangeSubscription((message, {runWhenAny, anyModelMatch}) => {
    runWhenAny(message, [anyModelMatch(['attempt'])], refetchAttempt);
  }, []);

  useEffect(() => {
    if (attempt && attempt.status === 'completed') {
      // @ts-expect-error TS2322 The API tags are injected in a way that TS doesn't know.
      actions.invalidateTags([{type: QUIZ_TAGS.ATTEMPT, id: 'list'}]);
      browserHistory.push(`${attempt.take_url}result/`);
    }
  }, [attempt]);

  if (attempt == null) return null;

  dateCreateAt.current = attempt.created_at;

  const handleAnswerQuestion = ({
    question,
    answer,
  }: {
    answer: AttemptAnswerType;
    question: QuestionType;
  }) => {
    answerQuestion({
      question_id: question.id,
      attempt_id: attempt.id,
      ...answer,
    });
  };

  const handleSubmitAttempt = () =>
    completeAttempt({
      attempt,
    })
      .unwrap()
      .then(() => {
        browserHistory.push(`${attempt.take_url}result/`);
      });

  return (
    <TakeQuizPage versionId={versionId} hashid={hashid} attemptId={attemptId}>
      <AttemptQuizContent
        attempt={attempt}
        content={quiz?.content}
        duration={quiz?.duration ?? undefined}
        onAnswer={handleAnswerQuestion}
        onSubmitAttempt={handleSubmitAttempt}
        isAnsweringQuestion={isAnsweringQuestion}
      />
    </TakeQuizPage>
  );
}
